import {
  BusinessUnitsIcon,
  ChartHistogramIcon,
  FinancesIcon,
  Hierarchy3Icon,
  ListDetailsIcon,
} from 'Tokens/Icons/Data';
import { FrameworkRefs } from '../Assessment';
import { InfoboxProps } from 'Atoms';
import { JSX } from 'react';

type LearnMoreStep = {
  title: string;
  description: string;
  icon: JSX.Element;
};

type OnboardingInformation = {
  [x in FrameworkRefs]: {
    title: string;
    learnMore: {
      generalInfo: string;
      steps: {
        company: LearnMoreStep[];
        group: LearnMoreStep[];
      };
    };
    companyStructure: {
      info: string;
    };
    materialityAssessment: {
      title: string;
      description?: string;
      info?: {
        steps: [
          {
            title?: string;
            description: string;
            infobox?: InfoboxProps;
          },
        ];
      };
      table: {
        title: string;
        description: string;
      };
    };
    dataCollectionSetup: {
      title: string;
      info: {
        steps: [
          {
            title: string;
            description: string;
          },
        ];
      };
      table: {
        title: string;
      };
    };
  };
};

export const ONBOARDING_INFORMATION: OnboardingInformation = {
  esrs: {
    title: 'ESRS',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia ESRS reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  vsme: {
    title: 'VSME',
    learnMore: {
      generalInfo:
        'Welcome to the VSME reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Choose applicable disclosures',
            description:
              'It is possible to choose applicable disclosures on group level, on subsidiary levels or both. If the group is to choose applicable disclosures, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the VSME requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Choose applicable disclosures',
            description:
              'It is possible to choose applicable disclosures on group level, on subsidiary levels or both. If the group is to choose applicable disclosures, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the VSME requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Choose applicable disclosures',
      description: 'Define topics, standards and metrics applicable for your company',
      table: {
        title: 'Assess VSME standards and metrics',
        description: 'Select which standards are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and recommended configurations',
            description: `Here, in the data collection setup, you can define how you want to collect data for your selected disclosures. You can choose the data gathering level, frequency and the data points breakdown. By default, data points are configured to be collected on company level and yearly. 
              Some of the data points are required by the VSME to be broken down and some are voluntary. Celsia platform uses the “tags” system to enable this breakdown (for example, in case you need to breakdown a data point by country, “Norway” would be a tag example).
              Use “Bulk edit” to change configurations across all topics at once, or enter and configure each topic individually.`,
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  ifrs: {
    title: 'IFRS',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia ESRS reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  tcfd: {
    title: 'TCFD',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia TCFD reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  'esrs-oil-and-gas': {
    title: 'ESRS Oil and Gas',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia ESRS Oil and Gas reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  'entity-specific': {
    title: 'Entity-specific',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia Entity-specific reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
  sasb: {
    title: 'SASB',
    learnMore: {
      generalInfo:
        'Welcome to the Celsia SASB reporting process. The onboarding flow will take you through the first steps of setting up your company structure and choosing material topics.',
      steps: {
        company: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding business units if needed.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'Materiality assessment is performed on company level. We normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
        group: [
          {
            title: 'Set company structure',
            description: 'Define the company structure by adding subsidiaries.',
            icon: <Hierarchy3Icon boxSize="20px" />,
          },
          {
            title: 'Materiality assessment',
            description:
              'It is possible to perform a materiality assessment on group level, on subsidiary levels or both. If the group is to perform a materiality assessment, we normally advise this to be the first step of the process.',
            icon: <ListDetailsIcon boxSize="20px" />,
          },
          {
            title: 'Data collection setup',
            description:
              'Decide which data points to collect from different parts of the company, and choose the data collection frequency and disaggregation of data points.',
            icon: <BusinessUnitsIcon boxSize="20px" />,
          },
          {
            title: 'Collect data',
            description:
              'Populate the selected data points in the report to ensure comprehensive and accurate reporting.',
            icon: <ChartHistogramIcon boxSize="20px" />,
          },
          {
            title: 'Use results',
            description:
              'Download your report following the CSRD requirements to use in external reporting.',
            icon: <FinancesIcon boxSize="20px" />,
          },
        ],
      },
    },
    companyStructure: {
      info: 'You can setup your organisational structure if you want to delegate parts of the data collection process to different departments',
    },
    materialityAssessment: {
      title: 'Materiality assessment',
      info: {
        steps: [
          {
            title: 'Step 1: Perform assessment',
            description: 'Understand and perform materiality assessment',
            infobox: {
              title: 'Use a materiality assessment template',
              description:
                'You should have received the DMA template from your Customer Success Manager. If not, please contact your manager to gain access to the template.',
              status: 'info',
              w: '100%',
              titleProps: { variant: 'h3' },
              closable: false,
              withIcon: false,
              backgroundColor: 'inherit',
              border: '1px solid',
              borderRadius: '8px',
              borderColor: '#8787FF',
            },
          },
        ],
      },
      table: {
        title: 'Step 2: Choose material topics',
        description: 'Select which topics are material for the company',
      },
    },
    dataCollectionSetup: {
      title: 'Data collection setup',
      info: {
        steps: [
          {
            title: 'Required and voluntary configurations',
            description:
              'In the data collection setup you can define how you want to collect data for your material data points. You can topic choose the data gathering level, frequency and how to disaggregate metrics. By default, data points are configured to be collected on the company level and yearly, and the disaggregation is not defined. You can use “Bulk editing” (below) to configure some metrics disaggregation across all topics at once. For other configurations, please, enter each topic individually and follow the instruction provided there.',
          },
        ],
      },
      table: {
        title: 'Topics to configure',
      },
    },
  },
};
